import axios from 'axios';
import { RegisterData, UserData, AuthResponse, LoginHistoryEntry, ChangePasswordData } from '../types';
import {
  Board,
  BoardWithDetails,
  BoardCreate,
  List,
  Card,
  CardWithDetails,
  Label,
  Checklist,
  CommentWithUser,
} from '../types/kanban';
import { useMemo } from 'react';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Extract the current route path from the hash (e.g. "#/boards/123" -> "/boards/123").
// With HashRouter the pathname is always "/", so the real route lives in the fragment.
const getRoutePath = () => {
  const hash = window.location.hash;
  return hash ? hash.replace(/^#/, '') : '/';
};

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      const currentPath = getRoutePath();
      if (!['/login', '/register'].includes(currentPath)) {
        // Hash-router login URL: the whole route + query string lives inside the hash
        window.location.href = `#/login?redirect=${encodeURIComponent(currentPath)}`;
      }
    }
    return Promise.reject(error);
  }
);

export function useApi() {
  return useMemo(
    () => ({
      // Auth
      login: async (email: string, password: string): Promise<AuthResponse> => {
        const response = await api.post<AuthResponse>('/auth/login', {
          email,
          password,
        });
        return response.data;
      },
      register: async (userData: RegisterData): Promise<AuthResponse> => {
        const response = await api.post<AuthResponse>('/auth/register', userData);
        return response.data;
      },
      getCurrentUser: async (): Promise<UserData> => {
        const response = await api.get<UserData>('/users/me');
        return response.data;
      },

      // Boards
      getBoards: async (): Promise<Board[]> => {
        const response = await api.get<Board[]>('/boards');
        return response.data;
      },
      getBoard: async (id: number): Promise<BoardWithDetails> => {
        const response = await api.get<BoardWithDetails>(`/boards/${id}`);
        return response.data;
      },
      createBoard: async (boardData: BoardCreate): Promise<Board> => {
        const response = await api.post<Board>('/boards', boardData);
        return response.data;
      },
      updateBoard: async (id: number, boardData: Partial<BoardCreate>): Promise<Board> => {
        const response = await api.put<Board>(`/boards/${id}`, boardData);
        return response.data;
      },
      deleteBoard: async (id: number): Promise<void> => {
        await api.delete(`/boards/${id}`);
      },

      // Lists
      createList: async (
        boardId: number,
        listData: { name: string; pos: number }
      ): Promise<List> => {
        const response = await api.post<List>(`/boards/${boardId}/lists`, listData);
        return response.data;
      },
      updateList: async (
        id: number,
        listData: { name: string; pos: number; closed?: boolean }
      ): Promise<List> => {
        const response = await api.put<List>(`/lists/${id}`, listData);
        return response.data;
      },
      deleteList: async (id: number): Promise<void> => {
        await api.delete(`/lists/${id}`);
      },

      // Cards
      createCard: async (
        listId: number,
        cardData: {
          name: string;
          description?: string;
          pos: number;
          due?: string | null;
          due_complete?: boolean;
          badges?: Record<string, any>;
          cover?: Record<string, any>;
          desc_data?: Record<string, any>;
        }
      ): Promise<Card> => {
        const response = await api.post<Card>(`/lists/${listId}/cards`, cardData);
        return response.data;
      },
      getCard: async (id: number): Promise<CardWithDetails> => {
        const response = await api.get<CardWithDetails>(`/cards/${id}`);
        return response.data;
      },
      updateCard: async (
        id: number,
        cardData: {
          name: string;
          description?: string;
          pos: number;
          due?: string | null;
          due_complete?: boolean;
          closed?: boolean;
          list_id?: number;
          badges?: Record<string, any>;
          cover?: Record<string, any>;
          desc_data?: Record<string, any>;
        }
      ): Promise<Card> => {
        const response = await api.put<Card>(`/cards/${id}`, cardData);
        return response.data;
      },
      deleteCard: async (id: number): Promise<void> => {
        await api.delete(`/cards/${id}`);
      },

      // Labels
      getLabels: async (boardId: number): Promise<Label[]> => {
        const response = await api.get<Label[]>(`/boards/${boardId}/labels`);
        return response.data;
      },
      createLabel: async (
        boardId: number,
        labelData: { name: string; color: string }
      ): Promise<Label> => {
        const response = await api.post<Label>(`/boards/${boardId}/labels`, labelData);
        return response.data;
      },
      addLabelToCard: async (cardId: number, labelId: number): Promise<any> => {
        const response = await api.post(`/cards/${cardId}/labels`, { label_id: labelId });
        return response.data;
      },
      removeLabelFromCard: async (cardId: number, labelId: number): Promise<void> => {
        await api.delete(`/cards/${cardId}/labels/${labelId}`);
      },

      // Checklists
      createChecklist: async (
        cardId: number,
        checklistData: { name: string; pos: number }
      ): Promise<Checklist> => {
        const response = await api.post<Checklist>(`/cards/${cardId}/checklists`, checklistData);
        return response.data;
      },
      deleteChecklist: async (id: number): Promise<void> => {
        await api.delete(`/checklists/${id}`);
      },

      // Check Items
      createCheckItem: async (
        checklistId: number,
        itemData: {
          name: string;
          pos: number;
          state: 'incomplete' | 'complete';
          due?: string | null;
        }
      ): Promise<any> => {
        const response = await api.post(`/checklists/${checklistId}/items`, itemData);
        return response.data;
      },
      updateCheckItem: async (
        id: number,
        itemData: {
          name: string;
          pos: number;
          state: 'incomplete' | 'complete';
          due?: string | null;
        }
      ): Promise<any> => {
        const response = await api.put(`/check-items/${id}`, itemData);
        return response.data;
      },
      deleteCheckItem: async (id: number): Promise<void> => {
        await api.delete(`/check-items/${id}`);
      },

      // Comments
      getComments: async (cardId: number): Promise<CommentWithUser[]> => {
        const response = await api.get<CommentWithUser[]>(`/cards/${cardId}/comments`);
        return response.data;
      },
      createComment: async (cardId: number, commentData: { text: string }): Promise<any> => {
        const response = await api.post(`/cards/${cardId}/comments`, commentData);
        return response.data;
      },
      updateComment: async (id: number, commentData: { text: string }): Promise<any> => {
        const response = await api.put(`/comments/${id}`, commentData);
        return response.data;
      },
      deleteComment: async (id: number): Promise<void> => {
        await api.delete(`/comments/${id}`);
      },

      // File Attachments
      getCardAttachments: async (cardId: number): Promise<any> => {
        const response = await api.get(`/cards/${cardId}/attachments`);
        return response.data;
      },
      uploadCardAttachment: async (
        cardId: number,
        file: File,
        entityType: string = 'Card'
      ): Promise<any> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', entityType);
        formData.append('entity_id', cardId.toString());

        const response = await api.post(`/cards/${cardId}/attachments`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      },
      deleteFile: async (fileUuid: string): Promise<void> => {
        await api.delete(`/files/${fileUuid}`);
      },
      getSecureImage: async (url: string): Promise<Blob> => {
        const response = await api.get(url, {
          responseType: 'blob',
        });
        return response.data;
      },

      // Epics
      getEpics: async (boardId: number): Promise<any> => {
        const response = await api.get(`/boards/${boardId}/epics`);
        return response.data;
      },
      createEpic: async (boardId: number, epicData: any): Promise<any> => {
        const response = await api.post(`/boards/${boardId}/epics`, epicData);
        return response.data;
      },
      getEpic: async (epicId: number): Promise<any> => {
        const response = await api.get(`/epics/${epicId}`);
        return response.data;
      },
      updateEpic: async (epicId: number, epicData: any): Promise<any> => {
        const response = await api.put(`/epics/${epicId}`, epicData);
        return response.data;
      },
      deleteEpic: async (epicId: number): Promise<void> => {
        await api.delete(`/epics/${epicId}`);
      },
      addEpicToCard: async (cardId: number, epicId: number): Promise<any> => {
        const response = await api.post(`/cards/${cardId}/epics`, { epic_id: epicId });
        return response.data;
      },
      removeEpicFromCard: async (cardId: number, epicId: number): Promise<void> => {
        await api.delete(`/cards/${cardId}/epics/${epicId}`);
      },

      // Auth - Password & History
      changePassword: async (data: ChangePasswordData): Promise<{ message: string }> => {
        const response = await api.put<{ message: string }>('/users/me/password', data);
        return response.data;
      },
      getLoginHistory: async (limit: number = 20): Promise<LoginHistoryEntry[]> => {
        const response = await api.get<LoginHistoryEntry[]>(`/users/me/login-history?limit=${limit}`);
        return response.data;
      },

      // Card Links
      getCardLinks: async (cardId: number): Promise<any> => {
        const response = await api.get(`/cards/${cardId}/links`);
        return response.data;
      },
      createCardLink: async (parentCardId: number, childCardId: number): Promise<any> => {
        const response = await api.post(`/cards/${parentCardId}/links`, {
          child_card_id: childCardId,
        });
        return response.data;
      },
      createLinkedCard: async (
        parentCardId: number,
        data: {
          name: string;
          list_id: number;
          description?: string;
          copy_labels?: boolean;
          copy_epics?: boolean;
        }
      ): Promise<any> => {
        const response = await api.post(`/cards/${parentCardId}/linked-cards`, data);
        return response.data;
      },
      deleteCardLink: async (linkId: number): Promise<void> => {
        await api.delete(`/card-links/${linkId}`);
      },

      // Wikis
      getWikis: async (boardId: number): Promise<any> => {
        const response = await api.get(`/boards/${boardId}/wikis`);
        return response.data;
      },
      createWiki: async (boardId: number, wikiData: any): Promise<any> => {
        const response = await api.post(`/boards/${boardId}/wikis`, wikiData);
        return response.data;
      },
      getWiki: async (wikiId: number): Promise<any> => {
        const response = await api.get(`/wikis/${wikiId}`);
        return response.data;
      },
      updateWiki: async (wikiId: number, wikiData: any): Promise<any> => {
        const response = await api.put(`/wikis/${wikiId}`, wikiData);
        return response.data;
      },
      deleteWiki: async (wikiId: number): Promise<void> => {
        await api.delete(`/wikis/${wikiId}`);
      },
    }),
    []
  );
}
